import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';
import { supabase } from '@/lib/supabase';

interface Article {
  id: string;
  title: string;
  title_zh_tw: string;
  slug: string;
  content: string;
  content_zh_tw: string;
  image_url: string;
  category_id: string;
  tags: string[];
  is_published: boolean;
  created_at: string;
  article_type: string;
  source_url: string;
  source_name: string;
  source_thanks_text: string;
}

interface Category {
  id: string;
  name: string;
  name_zh_tw: string;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function ArticleDetail() {
  const { t } = useTranslation();
  const { language } = useLanguageDetect();
  const { slug } = useParams<{ slug: string }>();

  const [article, setArticle] = useState<Article | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    async function fetchArticle() {
      if (!slug) return;
      try {
        const { data: articleData, error: articleError } = await supabase
          .from('articles')
          .select('id, title, title_zh_tw, slug, content, content_zh_tw, image_url, category_id, tags, is_published, created_at, article_type, source_url, source_name, source_thanks_text')
          .eq('slug', slug)
          .eq('is_published', true)
          .maybeSingle();

        if (articleError) throw articleError;
        if (!articleData) {
          setLoading(false);
          return;
        }

        setArticle(articleData);

        if (articleData.category_id) {
          const { data: categoryData, error: categoryError } = await supabase
            .from('categories')
            .select('id, name, name_zh_tw')
            .eq('id', articleData.category_id)
            .maybeSingle();

          if (!categoryError && categoryData) {
            setCategory(categoryData);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    }

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <main className="w-full">
        <section className="h-[300px] md:h-[420px] w-full bg-background-200 animate-pulse" />
        <section className="w-full py-10 md:py-14 bg-background-50 px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="w-24 h-6 bg-background-200 rounded-full animate-pulse mb-4" />
            <div className="w-full h-8 bg-background-200 rounded animate-pulse mb-6" />
            <div className="space-y-3">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="w-full h-4 bg-background-200 rounded animate-pulse" style={{ width: `${80 + Math.random() * 20}%` }} />
              ))}
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="w-full min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('loadError')}
          </h1>
          <p className="text-foreground-500 text-sm mb-6">{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); window.location.reload(); }}
            className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-xs" />
            {t('retry') || '重新加载'}
          </button>
        </div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="w-full min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {t('notFound')}
          </h1>
          <p className="text-foreground-500 text-sm mb-6">{t('notFoundDesc')}</p>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xs" />
            {t('backToHome')}
          </a>
        </div>
      </main>
    );
  }

  const displayTitle = language === 'zh-Hans' ? article.title : article.title_zh_tw;
  const displayContent = language === 'zh-Hans' ? article.content : article.content_zh_tw;
  const displayCategoryName = category
    ? (language === 'zh-Hans' ? category.name : category.name_zh_tw)
    : '';

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = typeof window !== 'undefined' ? encodeURIComponent(displayTitle + ' - 88健康知识') : '';

  const handleCopyLink = function () {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(function () {
        setCopySuccess(true);
        setTimeout(function () { setCopySuccess(false); }, 2000);
      });
    }
  };

  return (
    <main className="w-full">
      <section className="relative h-[300px] md:h-[420px] w-full overflow-hidden">
        <img
          alt={displayTitle}
          title={displayTitle}
          className="w-full h-full object-cover object-top"
          src={article.image_url}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-4xl mx-auto">
            {displayCategoryName && (
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-white mb-3">
                {displayCategoryName}
              </span>
            )}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
              {displayTitle}
            </h1>
            <p className="text-white/60 text-xs mt-3">
              {t('publishedOn')} {formatDate(article.created_at)}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full py-10 md:py-14 bg-background-50 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <div
            className="article-content text-sm md:text-base max-w-none"
            dangerouslySetInnerHTML={{ __html: displayContent }}
          />

          <div className="mt-8 pt-6 border-t border-background-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-400 font-medium">{t('tags')}:</span>
              {(article.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-800"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-6 pt-6 border-t border-background-200">
            <p className="text-xs text-foreground-400 font-medium mb-3">分享这篇文章</p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className={`${copySuccess ? 'ri-check-line text-green-500' : 'ri-file-copy-line'} text-sm`} />
                {copySuccess ? '已复制' : '复制链接'}
              </button>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="nofollow noopener"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-[#1877F2] text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-sm" />
                Facebook
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`}
                target="_blank"
                rel="nofollow noopener"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/5 hover:bg-black/10 text-foreground-800 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-fill text-sm" />
                X
              </a>
              <a
                href={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="nofollow noopener"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#06C755]/10 hover:bg-[#06C755]/20 text-[#06C755] text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-line-fill text-sm" />
                Line
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(displayTitle)}&body=${encodeURIComponent(shareUrl)}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-sm" />
                邮件
              </a>
            </div>
          </div>

          {article.article_type === 'embedded' && article.source_url && (
            <div className="mt-6 p-5 rounded-xl border border-background-300/60 bg-background-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-link text-accent-600 text-sm" />
                </div>
                <div className="flex-1">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                    文章来源
                  </h4>
                  {article.source_name && (
                    <p className="text-sm text-foreground-700 mb-1">
                      <strong>{article.source_name}</strong>
                    </p>
                  )}
                  <p className="text-xs text-foreground-500 mb-2">
                    {article.source_thanks_text || `特别感谢 ${article.source_name || '原文作者'} 提供本文的宝贵内容，本页面仅为引用展示，版权归原作者所有。`}
                  </p>
                  <a
                    href={article.source_url}
                    target="_blank"
                    rel="nofollow noopener"
                    className="inline-flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs" />
                    查看原文
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-900 px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xs" />
              {t('backToHome')}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}